// O plugin-app do create-app vem com uma SignInPage padrao hardcoded pra
// providers: ["guest"] (ver node_modules/@backstage/plugin-app/dist/extensions/
// DefaultSignInPage.esm.js) - isso e independente do que o backend registra,
// entao remover o guest do backend nao tira o botao guest da tela de login.
// Sobrescrevemos a extensao com nossa propria SignInPage, so com Keycloak.
// IMPORTANTE: precisa estar dentro de um createFrontendModule com
// pluginId: 'app' - sem isso o Backstage ignora a extensao silenciosamente
// e mantem a padrao (confirmado na doc oficial de auth do backstage.io).
//
// As cores/tema abaixo sao só um EXEMPLO de como customizar visualmente o
// portal - troque pelas cores da sua marca (ou remova o createUnifiedTheme e
// use os temas padrão do Backstage).
import { createApp } from '@backstage/frontend-defaults';
import {
  createFrontendModule,
  createApiRef,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import explorePlugin from '@backstage-community/plugin-explore/alpha';
// Kubernetes e Kafka: os plugins de infra com um servico de verdade pra
// apontar hoje (o proprio cluster k3s deste projeto - ver infra/k3s/ - e o
// broker em infra/kafka/). GitHub Actions nao entra: o fluxo da plataforma
// (repo, build, imagem, deploy, docs no MinIO) nao usa CI do GitHub. Grafana/Jaeger
// foram so instalados (Dockerfile), sem entrar aqui, ate existir um servico
// real pra eles - ver README.md, secao "Plugins".
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import kafkaPlugin from '@backstage-community/plugin-kafka/alpha';
// Scaffolder: o pacote (frontend e backend) ja vem por padrao no create-app,
// e o backend ja registra @backstage/plugin-scaffolder-backend sozinho
// (packages/backend/src/index.ts, secao "// scaffolder plugin", mesmo padrao
// do kubernetes-backend) - so faltava isto aqui, sem o que o item de nav
// "page:scaffolder" (ja reservado em modules/nav/Sidebar.tsx) fica vazio e a
// rota /create nao existe. OwnerPicker (usado no template generic-app-completo) vem
// junto, embutido neste mesmo pacote, sem import separado.
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { navModule } from './modules/nav';
import { homeModule } from './modules/home';
import { usersModule } from './modules/users';
import { teamsModule } from './modules/teams';
import { domainsModule } from './modules/domains';
import { imagesModule } from './modules/images';
import { pipelinesModule } from './modules/pipelines';
import { appsModule } from './modules/apps';
import { scaffolderFieldsModule } from './modules/scaffolderFields';
import { techRadarPlugin } from './modules/techRadar';
import { techdocsProviderLinkModule } from './modules/techdocsProviderLink';
import { explorePageModule } from './modules/explorePage';
import { catalogKindsModule } from './modules/catalogKinds';
import { landingModule } from './modules/landing';
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPageBlueprint, ThemeBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  createUnifiedTheme,
  UnifiedThemeProvider,
  palettes,
  genPageTheme,
  pageTheme,
  shapes,
} from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

// Provider generico OIDC rebadged como Keycloak - nao existe apiRef pronto
// pra isso em @backstage/core-plugin-api (so pra providers "conhecidos" tipo
// GitHub/Google), entao criamos o nosso proprio. O provider.id passado pro
// OAuth2.create() TEM que ser 'oidc' (e o nome fixo da estrategia generica do
// backend) - so o id do apiRef e do SignInPage abaixo sao livres.
const keycloakAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({ id: 'auth.keycloak' });

const keycloakAuthApi = ApiBlueprint.make({
  name: 'keycloak',
  params: defineParams =>
    defineParams({
      api: keycloakAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'Keycloak',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        provider={{
          id: 'keycloak-auth-provider',
          title: 'Keycloak',
          message: 'Sign in using Keycloak',
          apiRef: keycloakAuthApiRef,
        }}
      />
    ),
  },
});

const brandPageTheme = {
  ...pageTheme,
  home: genPageTheme({ colors: ['#334155', '#0EA5E9'], shape: shapes.wave }),
};

// O painel de log da task do Scaffolder (@backstage/plugin-scaffolder,
// componente compilado, não editável aqui) envolve o TaskLogStream num
// <Paper style={{ height: '100%' }}> dentro de um <Content> em flex-column -
// como esse Paper concorre por espaço com os outros blocos da página (steps,
// outputs, botões) e não usa flex:1, o "100%" nunca vira uma altura de
// verdade: o log encolhe pro minHeight interno (240px) e sobra área vazia
// cinza embaixo (foi assim que apareceu na tela). Sem acesso ao componente,
// o único jeito é sobrescrever via CSS global, mirando a combinação
// .MuiPaper-root + esse style inline exato - !important necessário pra
// vencer o style inline (que sempre ganha de regra externa sem !important).
// Frágil a mudanças internas do plugin em versões futuras do Backstage, mas
// hoje é o único Paper do app com esse style literal.
const scaffolderLogViewerFix = `
  .MuiPaper-root[style*="height: 100%"] {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 1 auto !important;
    height: auto !important;
    min-height: 0 !important;
  }
`;

const globalCssOverrides = scaffolderLogViewerFix;

const portalLight = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#334155' },
    secondary: { main: '#0EA5E9' },
  },
  defaultPageTheme: 'home',
  pageTheme: brandPageTheme,
  components: {
    MuiCssBaseline: { styleOverrides: globalCssOverrides },
  },
});

const portalDark = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#64748B' },
    secondary: { main: '#38BDF8' },
  },
  defaultPageTheme: 'home',
  pageTheme: brandPageTheme,
  components: {
    MuiCssBaseline: { styleOverrides: globalCssOverrides },
  },
});

const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={portalLight}>{children}</UnifiedThemeProvider>
      ),
    },
  },
});

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={portalDark}>{children}</UnifiedThemeProvider>
      ),
    },
  },
});

export default createApp({
  features: [
    catalogPlugin,
    techRadarPlugin,
    explorePlugin,
    kubernetesPlugin,
    kafkaPlugin,
    scaffolderPlugin,
    navModule,
    landingModule,
    homeModule,
    usersModule,
    teamsModule,
    domainsModule,
    pipelinesModule,
    appsModule,
    imagesModule,
    scaffolderFieldsModule,
    techdocsProviderLinkModule,
    explorePageModule,
    catalogKindsModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [keycloakAuthApi, signInPage, lightTheme, darkTheme],
    }),
  ],
});
